/**
 * The one place the browser talks to the Worker.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────────
 * Sixty-one screens were transcribed to the pixel and exactly one of them made a network
 * call — `routes/search/client.ts` — and even that one posted to a relative `/v1`, which
 * from the Pages origin is `https://oprappo.com/v1/…`: the static site, not the API. So
 * the product had a complete face and no wiring, and the one wire that existed was
 * attached to the wrong thing.
 *
 * `VITE_API_BASE` is set by the deploy (`https://api.oprappo.com`) and nothing read it.
 * It is read here, once, and nowhere else.
 *
 * ── WHAT THIS MODULE REFUSES TO DO, AND WHY EACH REFUSAL MATTERS ────────────────
 *
 * IT NEVER COMPOSES BANGLA. docs/05 § conventions: "the client must never compose Bangla
 * from fragments." Every user-visible sentence arrives from the server already localised,
 * in `message_bn`. A client that builds a sentence out of a code and a noun produces
 * grammar no reviewer ever read, in a language most of this codebase's readers cannot
 * check.
 *
 * IT NEVER INVENTS A STATE. `packages/contracts` defines nine error codes because nine
 * screens were drawn, and it says plainly that there is deliberately no generic fallback:
 * the design has no something-went-wrong screen. The sentence itself is not quoted here —
 * `states/__tests__/states.test.tsx` forbids that string in every web and UI source file,
 * comments included, and it is right to: a generic sentence that exists as a quotable
 * constant is a generic sentence waiting to be rendered. So an unrecognised code is not
 * mapped to a shrug — it becomes `UnmappedApiError`, which is a bug in backend
 * information and is
 * meant to be noticed. Mapping it to a general failure screen is the one change that
 * would quietly undo docs/10 § WP-K's rule and the test that guards it.
 *
 * IT NEVER RETRIES A NON-IDEMPOTENT REQUEST. A retried POST to a money-moving route is
 * how one refund becomes two; `Idempotency-Key` exists on those routes precisely because
 * something, somewhere, will retry. Retrying is the caller's decision with the caller's
 * key, not a convenience hidden in a transport. (And on this API it would not even work:
 * `apps/api/src/http/idempotency.ts` STORES a 4xx under its key, so a replayed POST with
 * the same key would be answered with the stored refusal.)
 *
 * ── THE ONE THING IT DOES DO AGAIN: A GET AFTER AN EXPIRED ACCESS TOKEN ────────
 *
 * The access token lives in this module for minutes; the refresh token lives in storage
 * for ninety days. So the ordinary way a signed-in customer meets a 401 is not "signed
 * out" at all — it is an access token that ran out while the tab sat open. Until this
 * change that 401 went straight to the screen, and the screen showed অনুমতি নেই, headed
 * ফাউন্ডার মোড, to a customer looking at their own account.
 *
 * Now a 401 whose envelope says `no_identity` (`packages/contracts § ERROR_SUB_CODES`)
 * spends the refresh token ONCE — through `auth/session.ts § refreshSession`, which owns
 * the cross-tab lock and the rotation rules and is the only code allowed to touch the
 * refresh token — and then:
 *
 *   restored     a GET is sent again, once, with the new token. Reading twice is safe.
 *                Anything else is NOT sent again (above); it throws the original refusal,
 *                and because a session is now held the state layer renders no sign-in
 *                card for it — the next tap succeeds.
 *   no_token     nobody is signed in on this device.   → `isSignedOut(error)` is true
 *   rejected     the device's session is over.         → `isSignedOut(error)` is true
 *   unreachable  the renewal itself never arrived. That is the network, not the person,
 *                so it is a `NetworkError` and the screen is ইন্টারনেট নেই — never a
 *                sign-in card for someone who is signed in and on a bad connection.
 *
 * Concurrent 401s share one renewal: a screen that fires four GETs when the token has
 * expired spends the refresh token once, not four times, and a 401 for a token that was
 * already replaced while it was in flight is simply replayed with the new one.
 */
import type { ApiErrorBody, ErrorCode } from '@oprappo/contracts';
import { ERROR_CODES, isNoIdentity } from '@oprappo/contracts';

/**
 * Where the API lives.
 *
 * Falls back to a relative `/v1` only for the dev server, which proxies. In a built
 * bundle an unset base is a misconfiguration that would send every call to the static
 * host, so it is reported rather than absorbed — see `assertConfigured`.
 */
const RAW_BASE: string = import.meta.env['VITE_API_BASE'] ?? '';
export const API_BASE: string = RAW_BASE === '' ? '' : RAW_BASE.replace(/\/+$/, '');

/** A recognised, designed failure. Carries the server's own Bangla, never ours. */
export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly messageBn: string;
  readonly messageEn: string;
  readonly reference: string;
  readonly status: number;
  readonly body: ApiErrorBody;
  /**
   * True when this transport established that NOBODY IS SIGNED IN on this device: the
   * server refused for want of an identity AND the session could not be renewed. Read it
   * through `isSignedOut`, never by re-deriving it from `status` — a 401 on its own is
   * usually just an access token that ran out, which this module has already dealt with.
   */
  readonly signedOut: boolean;

  constructor(status: number, body: ApiErrorBody, options: { signedOut?: boolean } = {}) {
    super(`${body.error.code} (${body.error.reference})`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code = body.error.code;
    this.messageBn = body.error.message_bn;
    this.messageEn = body.error.message_en;
    this.reference = body.error.reference;
    this.signedOut = options.signedOut === true;
  }
}

/**
 * Did this failure end in "nobody is signed in"?
 *
 * The one question a screen needs answered to put the sign-in door in front of a customer
 * instead of a refusal. See the header for exactly when it is true.
 */
export function isSignedOut(cause: unknown): boolean {
  return cause instanceof ApiError && cause.signedOut;
}

/**
 * The same question, asked of the BODY a screen holds rather than of the thrown error —
 * which is all `states/boundary.tsx` ever sees (`data/resource.ts` hands it `error.body`).
 *
 * True when the server refused for want of an identity AND this device holds no session
 * NOW. The second half keeps a refusal the transport would not replay — a POST sent with a
 * token that expired in flight, answered after the renewal succeeded — from putting a
 * sign-in card in front of somebody who is, by the time it renders, signed in.
 */
export function refusalMeansSignedOut(body: ApiErrorBody): boolean {
  return isNoIdentity(body) && accessToken === null;
}

/**
 * The server answered with a failure this client has no screen for.
 *
 * Deliberately its own class rather than an `ApiError` with a made-up code. A surface
 * catching this has to decide what to do about a state nobody designed, which is the
 * decision that should be surfaced rather than smoothed over.
 */
export class UnmappedApiError extends Error {
  readonly status: number;
  readonly payload: unknown;
  constructor(status: number, payload: unknown) {
    super(`the server returned a failure with no designed state (HTTP ${String(status)})`);
    this.name = 'UnmappedApiError';
    this.status = status;
    this.payload = payload;
  }
}

/** The request never left, or the answer never arrived. Maps to `E_OFFLINE`'s screen. */
export class NetworkError extends Error {
  override readonly cause: unknown;
  constructor(cause: unknown) {
    super('the request did not reach the API');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null) return false;
  const err = (value as { error?: unknown }).error;
  if (typeof err !== 'object' || err === null) return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === 'string' && (ERROR_CODES as readonly string[]).includes(code);
}

/** Where the access token comes from. Set once by the auth flow; read on every call. */
let accessToken: string | null = null;

/**
 * Hand the client the current access token, or `null` on sign-out.
 *
 * A function rather than a value read at import time: a module-level constant captured
 * before sign-in would send the empty string forever, and captured after sign-out would
 * keep sending a revoked one.
 */
export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function currentAccessToken(): string | null {
  return accessToken;
}

/* ════════════════════════════════════════════════ renewal after a 401 ══ */

/** What renewing the session can end in. The same four `auth/session.ts` reports. */
export type RenewalOutcome = 'restored' | 'no_token' | 'rejected' | 'unreachable';

/**
 * How to renew, handed in by `auth/session.ts` when it loads.
 *
 * INJECTED RATHER THAN IMPORTED, because the session module already imports this one to
 * send the refresh request and to hand over the access token; importing it back would be
 * a cycle, and a cycle between the two files that hold the credentials is the one place a
 * half-initialised module would cost the most. With nothing bound — a test that imports
 * only the transport — a 401 simply ends in `no_token`: signed out, which is true of a
 * process that has no way to sign in.
 */
let renewer: (() => Promise<RenewalOutcome>) | null = null;

export function bindSessionRenewal(renew: (() => Promise<RenewalOutcome>) | null): void {
  renewer = renew;
}

/**
 * The transport's half of the single flight.
 *
 * `refreshSession()` has its own lock, and it clears the moment it settles. This one makes
 * the 401s that arrive WHILE a renewal is running wait for that renewal rather than start
 * a second one. The 401s that arrive just AFTER it — sent with the old token, answered
 * late — never reach here at all: `send` sees that the token it sent is no longer the one
 * held, and replays with the new one instead of spending the freshly rotated refresh
 * token again. Between the two, a burst of four expired GETs costs one renewal.
 */
let renewing: Promise<RenewalOutcome> | null = null;

function renewOnce(): Promise<RenewalOutcome> {
  if (renewing !== null) return renewing;
  const run = (renewer === null ? Promise.resolve<RenewalOutcome>('no_token') : renewer())
    .catch((): RenewalOutcome => 'unreachable')
    .finally(() => {
      renewing = null;
    });
  renewing = run;
  return run;
}

/**
 * Is this refusal the server saying there is no identity on the request?
 *
 * ONLY A 401. `E_PERMISSION` also travels as a 404 (a row that is not yours) and a 422 (a
 * malformed body, `apps/api/src/http/validate.ts`); neither is about who is asking, and
 * renewing a session over a typo would spend a refresh token for nothing.
 *
 * `sub_code` absent counts, and deliberately: an envelope from a Worker older than the
 * field, or written with `c.json` by a route that never learned it, is almost always the
 * expired access token this exists for — and the only cost of being wrong is one renewal
 * that finds the session fine. `role_denied` never counts: renewing a session does not
 * change what that person's role reaches.
 */
function refusesIdentity(status: number, body: ApiErrorBody): boolean {
  return status === 401 && body.error.code === 'E_PERMISSION' && body.error.sub_code !== 'role_denied';
}

/**
 * The refusal, as it is thrown once this device is known to hold no session.
 *
 * `sub_code: 'no_identity'` is written into the body when the server left it out (an
 * older Worker). Not invention: the transport has just ESTABLISHED it — the server refused
 * for want of an identity, and there is no session on this device to give it one — and
 * the body is the only thing the state layer reads.
 */
function signedOutError(status: number, body: ApiErrorBody): ApiError {
  const stamped: ApiErrorBody =
    body.error.sub_code === 'no_identity'
      ? body
      : { ...body, error: { ...body.error, sub_code: 'no_identity' } };
  return new ApiError(status, stamped, { signedOut: true });
}

export interface RequestOptions {
  /** Sent as `Idempotency-Key`. REQUIRED by the money-moving routes; refused elsewhere. */
  idempotencyKey?: string | undefined;
  signal?: AbortSignal | undefined;
  /** Send no Authorization header even when a token is held — the login door needs this. */
  anonymous?: boolean | undefined;
  /**
   * The founder-mode session token, sent as `x-oprappo-founder-session`.
   *
   * A NAMED FIELD AND NOT A `headers` BAG, DELIBERATELY. Founder mode is a second door:
   * `apps/api/src/founder/index.ts` requires a short-lived scoped token in a header of
   * its own, and WP-G explicitly rejected carrying it in `Authorization` — "putting it in
   * the same header would make a founder-mode token indistinguishable from a session
   * one". A general `headers` field would let any call attach any header, including this
   * token to a route that must never see it, and the value of this module is the small
   * set of things it will do. One named field can only do the one thing.
   *
   * It is per-call rather than module-level state like `accessToken`, for the same
   * reason: a token held here would ride along on every ordinary request, and founder
   * mode is a thing you are in for ten minutes on one screen, not a property of the tab.
   *
   * Requested by `apps/web/src/routes/founder/client.ts`, which wrote all eleven founder
   * calls and then REFUSED to send the ten guarded ones rather than issue a request the
   * server was certain to reject — a 401 the screen cannot tell from an expired session.
   * That refusal is why this field is here.
   */
  founderSession?: string | undefined;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** One round trip, before anything is decided about what it means. */
interface Answer {
  ok: boolean;
  status: number;
  payload: unknown;
}

/**
 * Send one request with exactly the token it is given, and read the answer.
 *
 * The token is a PARAMETER rather than read from `accessToken` here, because the 401 path
 * below has to know which token a request actually carried — the one held when it left,
 * which may not be the one held when it came back.
 */
async function exchange(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
  token: string | null,
): Promise<Answer> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (token !== null) headers['authorization'] = `Bearer ${token}`;
  if (options.idempotencyKey !== undefined) {
    headers['idempotency-key'] = options.idempotencyKey;
  }
  if (options.founderSession !== undefined) {
    headers['x-oprappo-founder-session'] = options.founderSession;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    });
  } catch (cause) {
    // An abort is the caller's own doing and must not be dressed up as an outage.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 204) return { ok: true, status: 204, payload: undefined };

  const text = await response.text();
  let payload: unknown = null;
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }
  return { ok: response.ok, status: response.status, payload };
}

/** An answer, as the caller receives it: the payload, or the failure it names. */
function settle<T>(answer: Answer): T {
  if (answer.ok) return answer.payload as T;
  if (isErrorBody(answer.payload)) throw new ApiError(answer.status, answer.payload);
  throw new UnmappedApiError(answer.status, answer.payload);
}

async function send<T>(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const sent = options.anonymous === true ? null : accessToken;
  const answer = await exchange(method, path, body, options, sent);

  // Everything but an identity refusal is exactly what it always was. An ANONYMOUS
  // request's 401 is too: it carried no session by design — the login door, and the
  // renewal's own POST, which must never renew itself — so it says nothing about
  // whether this device holds one.
  if (
    answer.ok ||
    options.anonymous === true ||
    !isErrorBody(answer.payload) ||
    !refusesIdentity(answer.status, answer.payload)
  ) {
    return settle<T>(answer);
  }
  return afterIdentityRefusal<T>(method, path, body, options, sent, answer.status, answer.payload);
}

/**
 * A 401 that says the request carried no identity. Four cases, in the order they are
 * tested, and the header's table is the same four from the other side:
 *
 *   1. The token was REPLACED while this request was in flight — another call renewed.
 *      Nothing to renew: a GET is sent once more with the new token; anything else
 *      throws the refusal, not signed out (a session is held, the next tap succeeds).
 *   2. No token was sent at all. Nobody is signed in on this device: signed out.
 *   3. The renewal failed or found nothing: signed out, and the access token the server
 *      has just refused is dropped, so nothing else sends it and the state layer can see
 *      that no session is held.
 *   4. The renewal could not be reached: `NetworkError` — offline, never signed out.
 *
 * Otherwise the session was restored, and a GET is sent ONCE more. Its answer is final
 * whatever it is: a second 401 is thrown as it comes, never renewed again, so no pair of
 * disagreeing clocks can turn this into a loop.
 */
async function afterIdentityRefusal<T>(
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions,
  sent: string | null,
  status: number,
  refusal: ApiErrorBody,
): Promise<T> {
  if (accessToken !== null && accessToken !== sent) {
    if (method !== 'GET') throw new ApiError(status, refusal);
    return settle<T>(await exchange(method, path, body, options, accessToken));
  }

  if (sent === null) throw signedOutError(status, refusal);

  const outcome = await renewOnce();
  if (outcome === 'unreachable') {
    throw new NetworkError(new Error('the session could not be renewed: the renewal never arrived'));
  }
  if (outcome !== 'restored') {
    if (accessToken === sent) accessToken = null;
    throw signedOutError(status, refusal);
  }

  if (method !== 'GET') throw new ApiError(status, refusal);
  return settle<T>(await exchange(method, path, body, options, accessToken));
}

export const api = {
  get: <T>(path: string, options?: RequestOptions): Promise<T> =>
    send<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    send<T>('POST', path, body, options),
  /**
   * PUT exists because a route does. `PUT /v1/notifications/prefs` is WP-M's own shape,
   * and NT2 could not reach it — the transport offered GET, POST, PATCH and DELETE, so
   * the route was answered by adding PATCH on the server to fit the client. That is the
   * tail wagging the dog: a transport missing a verb should grow the verb, not push the
   * API into a different shape to avoid it.
   */
  put: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    send<T>('PUT', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> =>
    send<T>('PATCH', path, body, options),
  delete: <T>(path: string, options?: RequestOptions): Promise<T> =>
    send<T>('DELETE', path, undefined, options),
} as const;

/**
 * True when this bundle knows where the API is.
 *
 * A built bundle with no `VITE_API_BASE` sends every call to the static host, which
 * answers 200 with `index.html` — so the failure is a JSON parse error somewhere far
 * from its cause. Surfaces that care can check this and say something true instead.
 */
export function isApiConfigured(): boolean {
  return API_BASE !== '' || import.meta.env.DEV;
}
